import { resumeSession } from '../api/client';
import { clearRecordedNcStorage } from './ncStorageRecord';

const NC_USER_URL = '/drive/ocs/v2.php/cloud/user?format=json';
export const NC_SSO_LOGIN_URL = '/drive/apps/user_oidc/login/1';
export const NC_SSO_LOCK_KEY = 'nc_sso_lock';
// 🔴 숨은 SSO iframe 은 쿠키만 받으면 된다 — 앱으로 보내면 앱 JS 를 받다가 버린다(10-01).
//    `/drive/status.php` 는 세션을 시작하지 않는 가벼운 페이지라 착지 비용이 거의 없다.
export const NC_SSO_HIDDEN_LANDING = '/drive/status.php';
const DEFAULT_TIMEOUT_MS = 20000;
const DEFAULT_LOCK_TTL_MS = 20000;
const DEFAULT_POLL_MS = 500;
const DEFAULT_PROBE_TIMEOUT_MS = 8000;
// SSO 브리지(/authorize)가 토큰을 쓰는 동안 만료되지 않을 여유.
const GATEWAY_TOKEN_MARGIN_MS = 60000;

let inFlight = null;

export class NcSsoError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'NcSsoError';
    this.code = code;
  }
}

function safeNow() {
  return Date.now();
}

function normalizeUid(uid) {
  if (uid === undefined || uid === null || uid === '') return null;
  return String(uid);
}

export function getGatewayUserId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return normalizeUid(user.id);
  } catch (e) {
    return null;
  }
}

export function getNcSsoUrl(redirectUrl) {
  if (!redirectUrl) return NC_SSO_LOGIN_URL;
  return `${NC_SSO_LOGIN_URL}?redirectUrl=${encodeURIComponent(redirectUrl)}`;
}

function remainingMs(deadline) {
  return Math.max(0, deadline - safeNow());
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getStorage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch (e) {
    return null;
  }
}

function readLock() {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(NC_SSO_LOCK_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeLock(owner) {
  const storage = getStorage();
  if (!storage) return false;
  try {
    storage.setItem(NC_SSO_LOCK_KEY, JSON.stringify({ owner, ts: safeNow() }));
    return true;
  } catch (e) {
    return false;
  }
}

function tryAcquireLock(lockTtlMs) {
  const storage = getStorage();
  if (!storage) return `memory:${safeNow()}:${Math.random()}`;
  const current = readLock();
  if (current && current.ts && safeNow() - Number(current.ts) < lockTtlMs) return null;
  const owner = `${safeNow()}:${Math.random().toString(36).slice(2)}`;
  if (!writeLock(owner)) return owner;
  const stored = readLock();
  return stored && stored.owner === owner ? owner : null;
}

function releaseLock(owner) {
  const storage = getStorage();
  if (!storage || !owner || owner.startsWith('memory:')) return;
  try {
    const current = readLock();
    if (current && current.owner === owner) storage.removeItem(NC_SSO_LOCK_KEY);
  } catch (e) {
    /* ignore */
  }
}

function startLockRefresh(owner, lockTtlMs) {
  if (!owner || owner.startsWith('memory:')) return null;
  const intervalMs = Math.max(1000, Math.floor(lockTtlMs / 4));
  return setInterval(() => {
    const current = readLock();
    if (current && current.owner === owner) writeLock(owner);
  }, intervalMs);
}

function isExpectedSession(session, expectedUid) {
  return !!(session && normalizeUid(session.uid) === expectedUid);
}

// NC 세션 상태를 한 번 묻는다: {state:'ok', uid, raw} | {state:'none', uid:null} | {state:'unreachable', uid:null}.
// 🔴 'unreachable' 은 fetch 예외·abort(타임아웃)이거나 502/503/504 일 때만이다 — 서버가 죽어 있는데
//    '세션 없음' 으로 읽으면 SSO 를 띄우고 20초 뒤에야 실패한다. 401/404/그 밖은 예전처럼 'none'.
export async function probeNcSessionState(options = {}) {
  const timeoutMs = Number(options.timeoutMs || DEFAULT_PROBE_TIMEOUT_MS);
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => { try { controller.abort(); } catch (e) { /* */ } }, timeoutMs) : null;
  let r = null;
  try {
    const init = {
      credentials: 'include',
      headers: { 'OCS-APIRequest': 'true', Accept: 'application/json' },
    };
    if (controller) init.signal = controller.signal;
    r = await fetch(NC_USER_URL, init);
  } catch (e) {
    return { state: 'unreachable', uid: null };
  } finally {
    if (timer) clearTimeout(timer);
  }
  const status = r && r.status;
  if (status === 502 || status === 503 || status === 504) return { state: 'unreachable', uid: null };
  if (!r || status === 401 || status === 404 || !r.ok) return { state: 'none', uid: null };
  try {
    const j = await r.json();
    const uid = j && j.ocs && j.ocs.data && j.ocs.data.id;
    return uid ? { state: 'ok', uid: normalizeUid(uid), raw: j } : { state: 'none', uid: null };
  } catch (e) {
    return { state: 'none', uid: null };
  }
}

// 반환 계약은 그대로다: {uid, raw} | null(세션 없음·확인 불가).
export async function getNcSessionUser() {
  const probe = await probeNcSessionState();
  return probe.state === 'ok' ? { uid: probe.uid, raw: probe.raw } : null;
}

// JWT exp(ms). 못 읽으면 null — App.jsx hasLiveToken 과 같이 «만료 시각을 모르면 살아 있다» 로 본다.
function tokenExpMs(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const exp = Number(claims && claims.exp);
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

// SSO 를 띄우기 전에 게이트웨이 토큰을 확인한다 — 숨은 iframe 안의 SSO 브리지(AuthorizePage)는
// 토큰이 없거나 401 이면 `/login` 으로 가 버린다(그러면 20초를 헛되이 기다린다).
// 토큰 없음 = true(예전 동작 그대로 — 판단은 브리지에 맡긴다) · 만료까지 60초 넘게 남음 = true ·
// 아니면 resume(탭 안 single-flight) 결과.
// 🔴 resume 은 부모 창에서만 부른다 — 브리지에서 부르면 refresh 쿠키를 동시에 회전시켜 family 가 폐기된다.
export async function defaultEnsureGateway() {
  const storage = getStorage();
  let token = null;
  try {
    token = storage ? storage.getItem('token') : null;
  } catch (e) {
    token = null;
  }
  if (!token) return true;
  const exp = tokenExpMs(token);
  if (!exp || exp - safeNow() > GATEWAY_TOKEN_MARGIN_MS) return true;
  if (typeof resumeSession !== 'function') return false;
  try {
    const out = await resumeSession();
    return !!(out && out.ok);
  } catch (e) {
    return false;
  }
}

async function getRequestToken() {
  try {
    const r = await fetch('/drive/', { credentials: 'include' });
    if (!r.ok) return null;
    const html = await r.text();
    const m = html.match(/data-requesttoken="([^"]+)"/);
    return m ? m[1] : null;
  } catch (e) {
    return null;
  }
}

export async function logoutNcSession() {
  const token = await getRequestToken();
  const url = token
    ? `/drive/index.php/logout?requesttoken=${encodeURIComponent(token)}`
    : '/drive/index.php/logout';
  try {
    await fetch(url, { credentials: 'include', redirect: 'manual' });
  } catch (e) {
    /* best effort */
  } finally {
    // 🔴 fetch 로그아웃은 NC 문서를 띄우지 않아 NC 의 clear 코드가 돌지 않는다(Clear-Site-Data 는 nginx 가 뗀다) —
    //    앞 사용자가 NC 에서 쓴 키는 여기서 지운다. 성공·실패와 무관하다(lib/ncStorageRecord).
    clearRecordedNcStorage();
  }
}

// 🔴 낡은 NC 쿠키가 남아 있으면 NC 가 SameSite 보호 리다이렉트로 **깨진 주소**를 준다
//    (`/drive/index.php_oidc/login/1` — 실측 2026-08-25). 그 주소는 404 라 SSO 가 영영 안 끝나고,
//    재시도해도 같은 쿠키를 다시 보내 같은 곳으로 간다(마스터는 두 번 다 실패, 시크릿창은 성공).
//    쿠키는 `path=/drive` 라 스크립트가 못 지운다(HttpOnly) → **서버가 만료시켜 준다.**
//    실패해도 그냥 진행한다 — 지금보다 나빠질 일은 없다.
export async function defaultResetNcCookies() {
  try {
    const storage = getStorage();
    const token = storage ? storage.getItem('token') : null;
    if (!token) return;
    const api = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) || '';
    await fetch(`${api}/api/collab/nc-session-reset`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (e) {
    /* best effort */
  }
}

// 🔴 숨은 iframe 의 **최종** load 만 신호로 센다(2026-10-01 간헐 403).
//    SSO 체인은 `user_oidc/login/1` 303 → `/authorize`(게이트웨이 SPA, 200) → 그 페이지 JS 가
//    `user_oidc/code` 로 보냄 → 착지(`NC_SSO_HIDDEN_LANDING`) 순서다. 그래서 첫 load 는
//    중간 문서(about:blank · `/authorize` · `user_oidc/*`)에서 뜬다. 그때 폴링을 시작하면
//    쿠키 없는 익명 응답의 Set-Cookie 가 login/1 이 심은 세션 쿠키(OIDC state)를 덮어써
//    콜백이 403 을 낸다 — 고치려던 경쟁이 그대로 남는다.
function isIntermediateSsoDocument(iframe) {
  try {
    const loc = iframe.contentWindow.location;
    if (String(loc.href) === 'about:blank') return true;
    const path = String(loc.pathname || '');
    if (path === '/authorize') return true;
    if (path.startsWith('/drive/apps/user_oidc/')) return true;
    return false;
  } catch (e) {
    // 출처가 달라 읽을 수 없다 = NC 문서에 도착했다는 뜻이다(최종 load 로 본다).
    return false;
  }
}

// 숨은 iframe 이 게이트웨이 로그인 화면(`/login`)에 도착했다 = SSO 브리지가 토큰을 못 써서 튕겼다.
// 그 뒤로는 기다려도 쿠키가 생기지 않으니 곧바로 실패로 본다. 읽을 수 없으면(교차 출처) 아니라고 본다.
function isGatewayLoginDocument(iframe) {
  try {
    const path = String(iframe.contentWindow.location.pathname || '');
    return path === '/login' || path.startsWith('/login/');
  } catch (e) {
    return false;
  }
}

function defaultLaunchSso(ssoUrl) {
  if (typeof document === 'undefined' || !document.body) {
    throw new NcSsoError('dom_unavailable', 'Nextcloud SSO requires a browser document.');
  }
  const iframe = document.createElement('iframe');
  iframe.title = 'nextcloud-session';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;
  let resolveLoaded = null;
  // reject 는 하지 않는다 — load 가 안 와도 상한까지 기다렸다가 그냥 폴링한다.
  // 해결 값: undefined(최종 문서) | 'gateway_login'(게이트웨이 로그인 화면으로 튕김).
  const loaded = new Promise((resolve) => { resolveLoaded = resolve; });
  const settle = (value) => {
    if (resolveLoaded) {
      const resolve = resolveLoaded;
      resolveLoaded = null;
      resolve(value);
    }
  };
  const onLoad = () => {
    if (isGatewayLoginDocument(iframe)) { settle('gateway_login'); return; }
    if (isIntermediateSsoDocument(iframe)) return;   // 다음 load 를 기다린다
    settle(undefined);
  };
  // 🔴 리스너는 src 를 넣기 **전에** 붙인다 — 뒤에 붙이면 빠른 load 를 놓친다.
  iframe.addEventListener('load', onLoad);
  iframe.addEventListener('error', onLoad);
  iframe.src = ssoUrl;
  iframe.style.position = 'absolute';
  iframe.style.width = '1px';
  iframe.style.height = '1px';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.border = 'none';
  document.body.appendChild(iframe);
  const cleanup = () => {
    try {
      iframe.removeEventListener('load', onLoad);
      iframe.removeEventListener('error', onLoad);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    } catch (e) {
      /* ignore */
    }
  };
  return { cleanup, loaded };
}

// 🔴 첫 폴링은 iframe 이 SSO 체인을 끝낸 뒤에 나간다. 다만 끝까지 매달리지 않게 상한을 둔다
//    (`deadline - pollMs`) — 상한에 걸려도 실패가 아니라 그냥 폴링을 시작한다.
//    타이머는 반드시 치운다(약 19.5초짜리가 매달린 채 남지 않게).
// 반환 = load 신호의 값('gateway_login' 이면 튕긴 것). 상한·거절은 undefined.
function waitForLoadedSignal(loaded, maxWaitMs) {
  let timer = null;
  const capped = new Promise((resolve) => {
    timer = setTimeout(() => resolve(undefined), Math.max(0, maxWaitMs));
  });
  const done = (value) => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    return value;
  };
  return Promise.race([loaded, capped]).then(done, () => done(undefined));
}

async function waitForExpectedSession(expectedUid, deadline, pollMs) {
  let lastUid = null;
  // 🔴 남은 시간이 0 이어도 **최소 한 번은** 물어본다 — iframe load 를 상한까지 기다린 뒤라
  //    한 번도 안 묻고 timeout 을 던지면 이미 잡힌 세션을 버리게 된다.
  for (;;) {
    const session = await getNcSessionUser();
    if (isExpectedSession(session, expectedUid)) return session;
    if (session && session.uid) lastUid = session.uid;
    if (remainingMs(deadline) <= 0) break;
    await sleep(Math.min(pollMs, Math.max(1, remainingMs(deadline))));
  }
  if (lastUid && lastUid !== expectedUid) {
    throw new NcSsoError('uid_mismatch', `Nextcloud session user ${lastUid} does not match gateway user ${expectedUid}.`);
  }
  throw new NcSsoError('timeout', 'Nextcloud session was not established before timeout.');
}

async function ensureNcSessionInternal(options) {
  const timeoutMs = Number(options.timeoutMs || DEFAULT_TIMEOUT_MS);
  const lockTtlMs = Number(options.lockTtlMs || DEFAULT_LOCK_TTL_MS);
  const pollMs = Number(options.pollMs || DEFAULT_POLL_MS);
  const expectedUid = normalizeUid(options.expectedUid);
  if (!expectedUid) {
    throw new NcSsoError('missing_expected_user', 'Gateway user id is required before opening Nextcloud data.');
  }
  const deadline = safeNow() + timeoutMs;
  // 숨은 iframe 의 도착지는 항상 가벼운 페이지다 — options.redirectUrl 은 여기 쓰지 않는다
  // (보이는 iframe 은 EmbedPage 가 getNcSsoUrl 로 따로 앱 경로를 만든다).
  const ssoUrl = options.ssoUrl || getNcSsoUrl(NC_SSO_HIDDEN_LANDING);
  const launchSso = options.launchSso || defaultLaunchSso;
  const resetCookies = options.resetCookies || defaultResetNcCookies;
  const ensureGateway = typeof options.ensureGateway === 'function' ? options.ensureGateway : defaultEnsureGateway;
  let firstProbe = true;

  while (remainingMs(deadline) > 0) {
    // 🔴 첫 확인은 상태까지 본다 — 서버가 응답하지 않으면 SSO 를 띄우지 않고 곧바로 실패한다
    //    (예전엔 '세션 없음' 으로 읽고 숨은 SSO 를 띄워 20초 뒤에야 실패했다). fetch 횟수는 같다.
    const probe = await probeNcSessionState();
    if (firstProbe && probe.state === 'unreachable') {
      throw new NcSsoError('unreachable', 'Nextcloud did not respond.');
    }
    firstProbe = false;
    const current = probe.state === 'ok' ? { uid: probe.uid, raw: probe.raw } : null;
    if (isExpectedSession(current, expectedUid)) return current;

    const owner = tryAcquireLock(lockTtlMs);
    if (!owner) {
      await sleep(Math.min(pollMs, Math.max(1, remainingMs(deadline))));
      continue;
    }

    let cleanup = null;
    const refresh = startLockRefresh(owner, lockTtlMs);
    try {
      const lockedCurrent = await getNcSessionUser();
      if (isExpectedSession(lockedCurrent, expectedUid)) return lockedCurrent;
      if (lockedCurrent && lockedCurrent.uid && lockedCurrent.uid !== expectedUid) {
        await logoutNcSession();
      }
      // 🔴 SSO 브리지는 게이트웨이 토큰으로 code 를 받는다 — 토큰이 만료됐고 resume 도 안 되면
      //    숨은 iframe 이 `/login` 으로 튕길 뿐이니 SSO 를 띄우지 않고 곧바로 실패한다.
      let gatewayOk = false;
      try {
        gatewayOk = !!(await ensureGateway());
      } catch (e) {
        gatewayOk = false;
      }
      if (!gatewayOk) {
        throw new NcSsoError('gateway_session', 'Gateway session is not usable for Nextcloud SSO.');
      }
      // 🔴 여기까지 왔다는 건 '쓸 수 있는 세션이 없다' 는 뜻이다 — 그러면 남아 있는 NC 쿠키는
      //    쓸모가 없을 뿐 아니라 **흐름을 깨뜨린다**(깨진 리다이렉트). SSO 를 띄우기 전에 지운다.
      //    🔴 정리가 실패해도 SSO 는 진행한다 — 여기서 예외를 흘리면 '고치려던 기능'이
      //    네트워크 한 번 튀는 것만으로 드라이브를 통째로 막는다(테스트가 잡았다).
      try {
        await resetCookies();
      } catch (e) {
        /* best effort */
      }
      // 호출처가 '재인증이 시작됐다' 를 알고 싶을 때(만료 보고) — 실패해도 SSO 는 그대로 간다.
      if (typeof options.onReauthStart === 'function') {
        try { options.onReauthStart(); } catch (e) { /* ignore */ }
      }
      // launchSso 반환 모양 세 가지를 다 받는다: 함수(옛 모양) · {cleanup,loaded} · undefined.
      const launched = await Promise.resolve(launchSso(ssoUrl));
      let loaded = null;
      if (typeof launched === 'function') {
        cleanup = launched;                       // 옛 모양 — load 신호가 없으니 곧바로 폴링한다
      } else if (launched && typeof launched === 'object') {
        if (typeof launched.cleanup === 'function') cleanup = launched.cleanup;
        if (launched.loaded && typeof launched.loaded.then === 'function') loaded = launched.loaded;
      }
      if (loaded) {
        const signal = await waitForLoadedSignal(loaded, remainingMs(deadline) - pollMs);
        if (signal === 'gateway_login') {
          throw new NcSsoError('gateway_session', 'Nextcloud SSO bounced to the gateway login page.');
        }
      }
      // 숨은 SSO 로 얻은 세션이라는 표식 — 이미 떠 있는 iframe 은 옛 문서라 호출처가 다시 불러온다.
      const session = await waitForExpectedSession(expectedUid, deadline, pollMs);
      return { ...session, reauthed: true };
    } finally {
      if (typeof cleanup === 'function') cleanup();
      if (refresh) clearInterval(refresh);
      releaseLock(owner);
    }
  }

  throw new NcSsoError('timeout', 'Nextcloud session was not established before timeout.');
}

export function ensureNcSession(options = {}) {
  const expectedUid = normalizeUid(
    Object.prototype.hasOwnProperty.call(options, 'expectedUid')
      ? options.expectedUid
      : getGatewayUserId()
  );
  const key = expectedUid || '__missing__';

  if (inFlight) {
    if (inFlight.expectedUid === key) return inFlight.promise;
    return inFlight.promise.catch(() => null).then(() => ensureNcSession(options));
  }

  const promise = ensureNcSessionInternal({ ...options, expectedUid })
    .finally(() => {
      if (inFlight && inFlight.promise === promise) inFlight = null;
    });
  inFlight = { expectedUid: key, promise };
  return promise;
}

export function resetNcSsoForTests() {
  inFlight = null;
  const storage = getStorage();
  if (storage) {
    try { storage.removeItem(NC_SSO_LOCK_KEY); } catch (e) { /* ignore */ }
  }
}
